import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, X } from 'lucide-react';

const CONSENT_KEY = 'privacy_terms_accepted';

const PrivacyConsentModal = ({ forceShow = false, onAccept }) => {
  const [open, setOpen] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const accepted = localStorage.getItem(CONSENT_KEY);
    if (!accepted || forceShow) setOpen(true);
  }, [forceShow]);

  // Focus trap for accessibility
  useEffect(() => {
    if (open && modalRef.current) {
      const focusableEls = modalRef.current.querySelectorAll('button, [href], input, textarea, [tabindex]:not([tabindex="-1"])');
      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];
      const trap = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (document.activeElement === firstEl) {
              e.preventDefault();
              lastEl.focus();
            }
          } else {
            if (document.activeElement === lastEl) {
              e.preventDefault();
              firstEl.focus();
            }
          }
        }
      };
      modalRef.current.addEventListener('keydown', trap);
      firstEl && firstEl.focus();
      return () => {
        if (modalRef.current) {
          modalRef.current.removeEventListener('keydown', trap);
        }
      };
    }
  }, [open]);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'true');
    setOpen(false);
    if (onAccept) onAccept();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-white/50 backdrop-blur-md" aria-modal="true" role="dialog" aria-labelledby="privacy-consent-title" aria-describedby="privacy-consent-desc">
      <div
        ref={modalRef}
        className="w-[95%] max-w-md mx-auto bg-white border border-gray-200 rounded-xl shadow-2xl p-6 m-4 relative animate-fade-in-up"
        tabIndex={-1}
      >
        {!forceShow && (
          <button
            className="absolute top-4 right-4 text-gray-400 hover:text-black transition-colors"
            onClick={() => setOpen(false)}
            aria-label="Close consent dialog"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="flex flex-col items-center text-center space-y-4">
          <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center border border-gray-100">
            <Shield className="w-6 h-6 text-black fill-current" />
          </div>

          <div className="space-y-2">
            <h2 id="privacy-consent-title" className="text-xl font-bold text-gray-900 tracking-tight">Privacy & Terms</h2>
            <p id="privacy-consent-desc" className="text-gray-500 text-sm leading-relaxed">
              We respect your privacy. By accessing our platform, you acknowledge and agree to our
              <Link to="/privacy" className="text-black font-semibold underline underline-offset-2 mx-1 hover:text-gray-600">Privacy Policy</Link>
              and
              <Link to="/terms" className="text-black font-semibold underline underline-offset-2 mx-1 hover:text-gray-600">Terms of Service</Link>.
            </p>
          </div>

          <button
            onClick={handleAccept}
            className="w-full py-3.5 px-4 bg-black text-white text-sm font-bold uppercase tracking-wider rounded-xl shadow-lg hover:bg-gray-900 active:scale-[0.98] transition-all duration-200"
            aria-label="Accept privacy and terms"
          >
            Accept & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyConsentModal;